import { Component, Input, Output, EventEmitter, forwardRef, ElementRef, ViewChild, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './date-range-picker.component.html',
  styleUrl: './date-range-picker.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true
    }
  ]
})
export class DateRangePickerComponent implements ControlValueAccessor, OnInit, AfterViewInit, OnDestroy {
  @Input() placeholder: string = 'Choose Date Range';
  @Input() styleClass: string = '';
  @Input() disabled: boolean = false;
  @Input() showIcon: boolean = true;
  @Input() dateFormat: string = 'dd/mm/yy';
  @Input() singleDateMode: boolean = false; // For single date selection
  @Input() minDate: Date | null = null; // Minimum selectable date
  @Input() maxDate: Date | null = null; // Maximum selectable date (defaults to today to disable future dates)
  /**
   * If true, future dates are disabled (default behavior for filters).
   * If false, future dates are allowed (needed for admin expiry selection).
   */
  @Input() disableFutureDates: boolean = true;
  /** When true, calendar dropdown opens above the input (for top fields to avoid overlapping fields below) */
  @Input() openAbove: boolean = false;

  @Output() onDateRangeChange = new EventEmitter<DateRange>();
  @Output() onOpen = new EventEmitter<void>();
  @Output() onClose = new EventEmitter<void>();

  dateRange: DateRange = { startDate: null, endDate: null };
  isOpen = false;
  currentMonth: Date = new Date();
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  hoverDate: Date | null = null;
  private isToggling = false; // Flag to prevent document click from interfering during toggle
  private dropdownOriginalParent: HTMLElement | null = null;
  private scrollListener: (() => void) | null = null;
  
  @ViewChild('calendarDropdown', { static: false }) calendarDropdown!: ElementRef<HTMLDivElement>;
  @ViewChild('triggerElement', { static: false }) triggerElement!: ElementRef<HTMLDivElement>;
  
  private onChange = (value: DateRange | null) => {};
  private onTouched = () => {};

  constructor(
    private elementRef: ElementRef,
    private cdr: ChangeDetectorRef
  ) {}

  private _docClickHandler = (event: MouseEvent): void => {
    this.onDocumentClick(event);
  };

  ngOnInit(): void {
    document.addEventListener('click', this._docClickHandler, { capture: true });
  }

  ngAfterViewInit(): void {
    // Set maxDate to today only when future dates should be disabled.
    // If disableFutureDates=false, keep maxDate as null so future dates are allowed.
    if (this.disableFutureDates && this.maxDate === null) {
      const today = new Date();
      today.setHours(23, 59, 59, 999); // End of today
      this.maxDate = today;
    }
  }

  /** Capture phase so outside-click runs before sibling trigger stopPropagation prevents bubble to document. */
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const isInsideTrigger = this.elementRef.nativeElement.contains(target);
    const isInsideDropdown = this.calendarDropdown?.nativeElement?.contains(target);
    
    // Ignore document clicks while toggling to prevent interference
    if (this.isToggling) {
      return;
    }
    
    // Don't close if clicking inside the component or the dropdown (may be appended to body)
    if (!isInsideTrigger && !isInsideDropdown) {
      if (this.isOpen) {
        this.moveDropdownBackAndClose();
      }
    }
  }

  closeDropdown(): void {
    if (this.isOpen) {
      this.moveDropdownBackAndClose();
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this._docClickHandler, { capture: true });
    this.removeScrollListener();
    if (this.isOpen && this.calendarDropdown?.nativeElement) {
      const el = this.calendarDropdown.nativeElement;
      if (el.parentNode === document.body) {
        this.dropdownOriginalParent?.appendChild(el);
      }
    }
  }

  private moveDropdownBackAndClose(): void {
    if (this.calendarDropdown?.nativeElement && this.dropdownOriginalParent) {
      const el = this.calendarDropdown.nativeElement;
      if (el.parentNode === document.body) {
        this.dropdownOriginalParent.appendChild(el);
      }
      this.dropdownOriginalParent = null;
    }
    this.removeScrollListener();
    this.isOpen = false;
    this.onClose.emit();
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener('scroll', this.scrollListener, true);
      this.scrollListener = null;
    }
  }

  // Removed maxDate limitation to allow future date selection

  get displayValue(): string {
    if (this.singleDateMode) {
      return this.dateRange.startDate ? this.formatDate(this.dateRange.startDate) : '';
    }
    if (this.dateRange.startDate && this.dateRange.endDate) {
      return this.formatDateRange(this.dateRange.startDate, this.dateRange.endDate);
    } else if (this.dateRange.startDate) {
      return this.formatDate(this.dateRange.startDate) + ' - ...';
    }
    return '';
  }

  formatDate(date: Date | null | undefined): string {
    if (!date) {
      return '';
    }
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateForButton(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  formatDateRange(start: Date | null | undefined, end: Date | null | undefined): string {
    return `${this.formatDate(start)} to ${this.formatDate(end)}`;
  }

  toggleDropdown(event?: MouseEvent): void {
    if (!this.disabled) {
      // Prevent event from bubbling to document click handler
      if (event) {
        event.stopPropagation();
      }
      
      // Set flag to prevent document click from interfering
      this.isToggling = true;
      
      const wasOpen = this.isOpen;
      this.isOpen = !this.isOpen;
      
      if (this.isOpen && !wasOpen) {
        this.onTouched();
        this.onOpen.emit();
        // Initialize selection state
        this.selectedStartDate = this.dateRange.startDate;
        this.selectedEndDate = this.dateRange.endDate;
        
        // If no date is selected and minDate is set, show the month of minDate
        if (!this.dateRange.startDate && this.minDate) {
          this.currentMonth = new Date(this.minDate);
          this.currentMonth.setDate(1); // Set to first day of month
        } else if (!this.dateRange.startDate && !this.minDate) {
          // If no date and no minDate, show current month
          this.currentMonth = new Date();
          this.currentMonth.setDate(1);
        } else if (this.dateRange.startDate) {
          // If date is selected, show the month of the selected date
          this.currentMonth = new Date(this.dateRange.startDate);
          this.currentMonth.setDate(1);
        }
        
        // Use markForCheck to schedule change detection without triggering it immediately
        this.cdr.markForCheck();
        
        // Use requestAnimationFrame to ensure the DOM is updated before positioning
        // This avoids interfering with Angular's change detection cycle
        requestAnimationFrame(() => {
          // Double-check isOpen state before proceeding
          if (!this.isOpen) {
            this.isOpen = true;
            this.cdr.markForCheck();
          }
          
          // Use another requestAnimationFrame to ensure the calendar dropdown is rendered
          requestAnimationFrame(() => {
            if (this.calendarDropdown && this.isOpen) {
              this.positionCalendarDropdown();
            }
            // Reset flag after a delay to allow calendar to fully render and prevent document click from interfering
            setTimeout(() => {
              this.isToggling = false;
              this.cdr.markForCheck();
            }, 300);
          });
        });
      } else if (!this.isOpen && wasOpen) {
        this.moveDropdownBackAndClose();
        // Reset flag immediately when closing
        setTimeout(() => {
          this.isToggling = false;
        }, 0);
      } else {
        // Reset flag if no state change
        setTimeout(() => {
          this.isToggling = false;
        }, 0);
      }
    }
  }

  private positionCalendarDropdown(): void {
    if (!this.calendarDropdown || !this.triggerElement) {
      return;
    }

    const trigger = this.triggerElement.nativeElement;
    const dropdown = this.calendarDropdown.nativeElement;
    const triggerRect = trigger.getBoundingClientRect();

    // Append to body so calendar stays fixed when page/container scrolls (avoids ancestor overflow/transform)
    if (dropdown.parentNode !== document.body) {
      this.dropdownOriginalParent = dropdown.parentElement;
      document.body.appendChild(dropdown);
    }

    // Fixed positioning relative to viewport — calendar will not scroll with page
    dropdown.style.position = 'fixed';
    dropdown.style.left = `${triggerRect.left}px`;
    dropdown.style.minWidth = '320px';
    dropdown.style.maxWidth = '340px';
    dropdown.style.zIndex = '99999';

    if (this.openAbove) {
      dropdown.style.top = '';
      dropdown.style.bottom = `${window.innerHeight - triggerRect.top + 8}px`;
      dropdown.style.marginTop = '0';
      dropdown.style.marginBottom = '0';
    } else {
      dropdown.style.top = `${triggerRect.bottom + 4}px`;
      dropdown.style.bottom = '';
      dropdown.style.marginTop = '0';
      dropdown.style.marginBottom = '';
    }

    // Re-position on scroll so calendar stays aligned with trigger
    this.removeScrollListener();
    this.scrollListener = () => {
      if (this.isOpen && this.triggerElement) {
        this.positionCalendarDropdown();
      }
    };
    window.addEventListener('scroll', this.scrollListener, true);
  }

  getDaysInMonth(date: Date): Date[] {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days: Date[] = [];
    
    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    // Adjust to start week on Monday (0 = Monday, 6 = Sunday)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6; // Sunday becomes 6
    
    // Add days from previous month to fill the week (starting from Monday)
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = new Date(year, month, -i);
      days.push(prevDate);
    }
    
    // Add days of current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add days from next month to fill the week
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push(new Date(year, month + 1, day));
    }
    
    return days;
  }

  getMonthName(date: Date): string {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  getWeekDays(): string[] {
    return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  isPastDate(date: Date): boolean {
    // This method is kept for backward compatibility but now enables past dates
    // Use isFutureDate() or isDateDisabled() instead
    return false;
  }

  isFutureDate(date: Date): boolean {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // If future dates are allowed, none are considered "future disabled"
    if (!this.disableFutureDates) {
      return false;
    }

    // Get max date (defaults to today if not set)
    const maxDate = this.maxDate || new Date();
    const maxDateCheck = new Date(maxDate);
    maxDateCheck.setHours(23, 59, 59, 999); // End of the day
    
    // Disable dates after maxDate (future dates)
    if (checkDate > maxDateCheck) {
      return true;
    }
    
    return false;
  }

  isDateDisabled(date: Date): boolean {
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    
    // Disable dates before minDate (if set)
    if (this.minDate) {
      const minDateCheck = new Date(this.minDate);
      minDateCheck.setHours(0, 0, 0, 0);
      if (checkDate < minDateCheck) {
        return true;
      }
    }
    
    // Disable dates after maxDate (future dates) only when disableFutureDates=true
    if (this.disableFutureDates) {
      const maxDate = this.maxDate || new Date();
      const maxDateCheck = new Date(maxDate);
      maxDateCheck.setHours(23, 59, 59, 999);
      if (checkDate > maxDateCheck) {
        return true;
      }
    }
    
    return false;
  }
  
  isBeforeMinDate(date: Date): boolean {
    if (!this.minDate) return false;
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const minDateCheck = new Date(this.minDate);
    minDateCheck.setHours(0, 0, 0, 0);
    return checkDate < minDateCheck;
  }

  isInRange(date: Date): boolean {
    // Check preview mode (hovering)
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current >= Math.min(start, end) && current <= Math.max(start, end);
    }
    
    // Check complete range
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.selectedEndDate.getTime();
      const current = date.getTime();
      return current >= start && current <= end;
    }
    
    return false;
  }

  isStartDate(date: Date): boolean {
    if (!this.selectedStartDate) return false;
    return date.toDateString() === this.selectedStartDate.toDateString();
  }

  isEndDate(date: Date): boolean {
    if (!this.selectedEndDate) return false;
    return date.toDateString() === this.selectedEndDate.toDateString();
  }

  isOtherMonth(date: Date): boolean {
    return date.getMonth() !== this.currentMonth.getMonth();
  }

  selectDate(date: Date): void {
    if (this.isDateDisabled(date)) return;
    
    // Single date mode - close immediately after selection
    if (this.singleDateMode) {
      this.selectedStartDate = new Date(date);
      this.selectedEndDate = null;
      this.hoverDate = null;
      
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: null
      };
      this.onChange(this.dateRange);
      this.onDateRangeChange.emit(this.dateRange);
      this.moveDropdownBackAndClose();
      return;
    }
    
    // Range mode - original behavior
    if (!this.selectedStartDate || (this.selectedStartDate && this.selectedEndDate)) {
      // Start new selection
      this.selectedStartDate = new Date(date);
      this.selectedEndDate = null;
      this.hoverDate = null;
      
      // Update the model with just start date (don't emit yet - wait for end date)
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: null
      };
      this.onChange(this.dateRange);
    } else if (this.selectedStartDate && !this.selectedEndDate) {
      // Complete the range
      if (date < this.selectedStartDate) {
        // If selected date is before start, swap them
        this.selectedEndDate = new Date(this.selectedStartDate);
        this.selectedStartDate = new Date(date);
      } else {
        this.selectedEndDate = new Date(date);
      }
      
      // Update the model
      this.dateRange = {
        startDate: this.selectedStartDate,
        endDate: this.selectedEndDate
      };
      this.onChange(this.dateRange);
      // Emit event when both dates are selected - this will trigger API call
      this.onDateRangeChange.emit(this.dateRange);
      this.moveDropdownBackAndClose();
    }
  }

  onDateHover(date: Date): void {
    if (this.selectedStartDate && !this.selectedEndDate) {
      this.hoverDate = new Date(date);
    }
  }

  onDateLeave(): void {
    this.hoverDate = null;
  }

  previousMonth(): void {
    this.currentMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() - 1, 1);
  }

  canNavigateToNextMonth(): boolean {
    // If future dates are allowed, allow navigation freely
    if (!this.disableFutureDates) {
      return true;
    }

    const maxDate = this.maxDate || new Date();
    const nextMonth = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    // Check if the first day of next month is before or equal to maxDate
    return nextMonth <= maxDate;
  }

  nextMonth(): void {
    // Prevent navigation to future months
    if (!this.canNavigateToNextMonth()) {
      return;
    }
    const next = new Date(this.currentMonth.getFullYear(), this.currentMonth.getMonth() + 1, 1);
    this.currentMonth = next;
  }

  clear(): void {
    // Close the calendar if it's open before clearing
    const wasOpen = this.isOpen;
    if (wasOpen) {
      this.moveDropdownBackAndClose();
    }
    
    // Clear the date range
    this.dateRange = { startDate: null, endDate: null };
    this.selectedStartDate = null;
    this.selectedEndDate = null;
    this.hoverDate = null;
    this.onChange(null);
    this.onDateRangeChange.emit({ startDate: null, endDate: null });
    this.onTouched();
    
    // Reset toggling flag
    this.isToggling = false;
  }

  /**
   * Handle clear icon click - prevent event propagation and clear the date range
   */
  onClearClick(event: MouseEvent): void {
    // Stop propagation to prevent triggering toggleDropdown
    event.stopPropagation();
    event.preventDefault();
    
    // Clear the date range (this will also close the calendar if open)
    this.clear();
  }

  writeValue(value: DateRange | null): void {
    // Don't reset isOpen state when writeValue is called - it's only for updating the date range value
    if (value && value.startDate) {
      this.dateRange = {
        startDate: new Date(value.startDate),
        endDate: value.endDate ? new Date(value.endDate) : null
      };
      this.selectedStartDate = new Date(value.startDate);
      this.selectedEndDate = value.endDate ? new Date(value.endDate) : null;
      this.currentMonth = new Date(value.startDate);
    } else {
      this.dateRange = { startDate: null, endDate: null };
      this.selectedStartDate = null;
      this.selectedEndDate = null;
    }
  }

  registerOnChange(fn: (value: DateRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  trackByDate(index: number, date: Date): number {
    return date.getTime();
  }

  isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6; // Sunday or Saturday
  }

  isStartOfRange(date: Date): boolean {
    if (this.selectedStartDate && date.toDateString() === this.selectedStartDate.toDateString()) {
      return true;
    }
    // Also check if this is the start in preview mode
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current === Math.min(start, end);
    }
    return false;
  }

  isEndOfRange(date: Date): boolean {
    if (this.selectedEndDate && date.toDateString() === this.selectedEndDate.toDateString()) {
      return true;
    }
    // Also check if this is the end in preview mode
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      return current === Math.max(start, end);
    }
    return false;
  }

  isInRangeButNotStartOrEnd(date: Date): boolean {
    // Check if we're in preview mode (start selected, hovering for end)
    if (this.selectedStartDate && this.hoverDate && !this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.hoverDate.getTime();
      const current = date.getTime();
      const minTime = Math.min(start, end);
      const maxTime = Math.max(start, end);
      const isStart = date.toDateString() === this.selectedStartDate.toDateString();
      const isEnd = date.toDateString() === this.hoverDate.toDateString();
      return current > minTime && current < maxTime && !isStart && !isEnd;
    }
    
    // Check if we have a complete range
    if (this.selectedStartDate && this.selectedEndDate) {
      const start = this.selectedStartDate.getTime();
      const end = this.selectedEndDate.getTime();
      const current = date.getTime();
      return current > start && current < end;
    }
    
    return false;
  }

  getDateClasses(date: Date): string {
    const baseClasses = 'date-cell w-9 h-9 flex items-center justify-center text-sm font-medium cursor-pointer relative border-0 outline-none focus:outline-none';
    
    if (this.isDateDisabled(date)) {
      return `${baseClasses} is-disabled`;
    }
    
    if (this.isOtherMonth(date)) {
      return `${baseClasses} is-other-month`;
    }
    
    const isWeekend = this.isWeekend(date);
    const isStart = this.isStartOfRange(date);
    const isEnd = this.isEndOfRange(date);
    const isInRange = this.isInRangeButNotStartOrEnd(date);
    const isToday = this.isToday(date);
    
    // Start date - primary healthcare color
    if (isStart) {
      return `${baseClasses} is-selected-boundary text-white font-semibold z-10`;
    }
    
    // End date - primary healthcare color
    if (isEnd) {
      return `${baseClasses} is-selected-boundary text-white font-semibold z-10`;
    }
    
    // Dates in range (between start and end) - rectangular background
    if (isInRange) {
      if (isWeekend) {
        return `${baseClasses} is-in-range-weekend font-normal`;
      }
      return `${baseClasses} is-in-range font-normal`;
    }
    
    // Today - tinted primary marker
    if (isToday) {
      return `${baseClasses} is-today font-semibold`;
    }
    
    if (isWeekend) {
      return `${baseClasses} is-weekend`;
    }

    return `${baseClasses} is-default`;
  }
}
